import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import api from '../utils/api';
import { mergeStoredUserFromMeData } from '../utils/sessionUser';
import { AUTH_CHANGE_EVENT } from '../utils/auth';

/** Dispatched after login/logout so cached lists reload when auth changes (same-tab SPA navigation). */
export const REFERENCE_DATA_AUTH_EVENT = AUTH_CHANGE_EVENT;

const ReferenceDataContext = createContext(null);

const hasAuth = () => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user') || '{}');
    return Boolean(user?.token);
  } catch {
    return false;
  }
};

const extractJobTypeRows = (response) => {
  if (!response) return [];
  return Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response?.data?.data)
    ? response.data.data
    : Array.isArray(response?.results)
    ? response.results
    : Array.isArray(response)
    ? response
    : [];
};

const extractFeedbackParameterRows = (response) => {
  if (!response) return [];
  return Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
    ? response
    : [];
};

export const useReferenceData = () => {
  const ctx = useContext(ReferenceDataContext);
  if (!ctx) {
    throw new Error('useReferenceData must be used within ReferenceDataProvider');
  }
  return ctx;
};

export const ReferenceDataProvider = ({ children }) => {
  const [jobTypesResponse, setJobTypesResponse] = useState(null);
  const [feedbackParametersResponse, setFeedbackParametersResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async () => {
    if (!hasAuth()) {
      setJobTypesResponse(null);
      setFeedbackParametersResponse(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [meRes, jt, fp] = await Promise.all([
        api.getCurrentUser().catch(() => null),
        api.getJobTypes(),
        api.getFeedbackParameters(),
      ]);
      if (meRes?.success && meRes.data) {
        mergeStoredUserFromMeData(meRes.data);
      }
      setJobTypesResponse(jt);
      setFeedbackParametersResponse(fp);
    } catch (e) {
      setError(e);
      console.error('ReferenceDataProvider load failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    const onAuthChange = () => {
      loadAll();
    };
    window.addEventListener(REFERENCE_DATA_AUTH_EVENT, onAuthChange);
    return () => window.removeEventListener(REFERENCE_DATA_AUTH_EVENT, onAuthChange);
  }, [loadAll]);

  const jobTypeRows = useMemo(
    () => extractJobTypeRows(jobTypesResponse),
    [jobTypesResponse]
  );

  const feedbackParameterRows = useMemo(
    () => extractFeedbackParameterRows(feedbackParametersResponse),
    [feedbackParametersResponse]
  );

  const refreshJobTypes = useCallback(async () => {
    if (!hasAuth()) return;
    try {
      const jt = await api.getJobTypes();
      setJobTypesResponse(jt);
    } catch (e) {
      console.error('refreshJobTypes:', e);
    }
  }, []);

  const refreshFeedbackParameters = useCallback(async () => {
    if (!hasAuth()) return;
    try {
      const fp = await api.getFeedbackParameters();
      setFeedbackParametersResponse(fp);
    } catch (e) {
      console.error('refreshFeedbackParameters:', e);
    }
  }, []);

  const value = useMemo(
    () => ({
      jobTypesResponse,
      feedbackParametersResponse,
      jobTypeRows,
      feedbackParameterRows,
      loading,
      error,
      refreshJobTypes,
      refreshFeedbackParameters,
      refreshAll: loadAll,
    }),
    [
      jobTypesResponse,
      feedbackParametersResponse,
      jobTypeRows,
      feedbackParameterRows,
      loading,
      error,
      refreshJobTypes,
      refreshFeedbackParameters,
      loadAll,
    ]
  );

  return (
    <ReferenceDataContext.Provider value={value}>
      {children}
    </ReferenceDataContext.Provider>
  );
};
